import { Banner, BlockStack, Modal, TextField } from "@shopify/polaris";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError } from "../../api/client";
import { getMe } from "../../api/me";
import { createSupplier, type SupplierOption } from "../../api/purchase_orders";
import {
  genericRegistrationNoError,
  invoiceNoError,
  isValidGenericRegistrationNo,
  isValidInvoiceNo,
} from "../../utils/invoiceNo";
import { registrationConcept } from "../../utils/registrationLabel";

import { t } from "../../lib/i18n";

/**
 * 仕入先セレクトに混ぜる「＋ 新規仕入先を登録…」の sentinel 値。
 * これが選ばれたら割当 mutation には流さず /suppliers?add=1 へ遷移する。
 */
export const NEW_SUPPLIER_OPTION_VALUE = "__new-supplier__";

type AddSupplierModalProps = {
  open: boolean;
  onClose: () => void;
  /** 作成成功時（モーダルは自分で閉じる）。一覧側の追加フォーカス等に使う。 */
  onCreated?: (supplier: SupplierOption) => void;
};

/**
 * 仕入先の新規作成モーダル。SupplierSetupCard（注文書画面）と仕入先ページの
 * 両方から使う共有部品＝登録番号の国別分岐・検証・エラー表示を1箇所に持つ。
 * 入力 state はモーダル内で完結し、閉じるたびに捨てる（開き直しで前回値を残さない）。
 */
export function AddSupplierModal({ open, onClose, onCreated }: AddSupplierModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const meQuery = useQuery({ queryKey: ["me"], queryFn: getMe });
  const registration = registrationConcept(meQuery.data?.country_code);
  const isJapanStore = registration?.isJapanInvoiceSystem === true;

  const close = () => {
    setName("");
    setInvoiceNo("");
    setContactEmail("");
    createMutation.reset();
    onClose();
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createSupplier({
        name: name.trim(),
        invoice_registration_no: invoiceNo.trim() || null,
        contact_email: contactEmail.trim() || null,
      }),
    onSuccess: (supplier) => {
      queryClient.invalidateQueries({ queryKey: ["po-suppliers"] });
      queryClient.invalidateQueries({ queryKey: ["purchase-order-drafts"] });
      onCreated?.(supplier);
      close();
    },
  });

  // JP＝T+13桁を検証。非 JP＝形式を強制しない（列幅のみ）。概念なし＝欄自体を出さない。
  const invoiceError =
    registration === null
      ? undefined
      : isJapanStore
        ? invoiceNo.trim() !== "" && !isValidInvoiceNo(invoiceNo)
          ? invoiceNoError()
          : undefined
        : !isValidGenericRegistrationNo(invoiceNo)
          ? genericRegistrationNoError()
          : undefined;
  const canSubmit = name.trim() !== "" && invoiceError === undefined;
  const createErrorMessage =
    createMutation.error instanceof ApiError
      ? createMutation.error.detail
      : t("仕入先を追加できませんでした。時間をおいて再度お試しください。");

  return (
    <Modal
      open={open}
      onClose={close}
      title={t("仕入先を追加")}
      primaryAction={{
        content: t("追加"),
        onAction: () => createMutation.mutate(),
        loading: createMutation.isPending,
        disabled: !canSubmit,
      }}
      secondaryActions={[{ content: t("取消"), onAction: close }]}
    >
      <Modal.Section>
        <BlockStack gap="300">
          {createMutation.isError && (
            <Banner tone="critical">
              <p>{createErrorMessage}</p>
            </Banner>
          )}
          <TextField
            label={t("仕入先名")}
            value={name}
            onChange={setName}
            autoComplete="off"
            requiredIndicator
            placeholder={t("山田珈琲焙煎所")}
          />
          {/* 登録番号は国の概念があるときだけ（Step 3）。JP＝適格請求書、
              GB/DE/FR/AU/CA＝国の呼び名（書式は検証しない）、US 等＝出さない。 */}
          {isJapanStore ? (
            <TextField
              label={t("適格請求書 登録番号")}
              value={invoiceNo}
              onChange={setInvoiceNo}
              autoComplete="off"
              placeholder="T1234567890123"
              error={invoiceError}
              helpText={t("任意。未登録（免税事業者等）の場合は空欄のままにしてください。あとから登録できます。")}
            />
          ) : registration ? (
            <TextField
              label={registration.label}
              value={invoiceNo}
              onChange={setInvoiceNo}
              autoComplete="off"
              error={invoiceError}
              helpText={t("任意。発注書に表示されます。形式の自動チェックは行いません。")}
            />
          ) : null}
          <TextField
            label={t("発注書の送信先メールアドレス")}
            type="email"
            value={contactEmail}
            onChange={setContactEmail}
            autoComplete="email"
            placeholder="orders@example.com"
            helpText={t("任意。登録すると注文書画面から自動提案の発注書を送信できます。")}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
